/**
 * @file api-crm.ts
 * @description API-клиент CRM-core (phase09):
 *  Pipelines + Stages, Deals + move-to-stage, Activities + feed,
 *  LegalProfiles, DocumentTemplates, Documents (issue/sign/void).
 *
 * Все endpoints — под `/admin/crm/*` и `/admin/shop/legal-profiles`.
 * RBAC проверяется в API; здесь ничего не валидируем.
 */

import { apiDownload, apiFetch, type ApiFileResponse } from "./api-client";
import { invalidateLiveSiteIssues } from "./site-view/live-issues-store";

// ─── Enums ──────────────────────────────────────────────────────────────────

export type PipelineKind = "SALES_B2C" | "SALES_B2B" | "RETURNS" | "RECLAMATION" | "CUSTOM";
export type StageKind = "INITIAL" | "IN_PROGRESS" | "WON" | "LOST" | "CANCELLED" | "ON_HOLD";
export type ActivityKind = "NOTE" | "TASK" | "CALL" | "EMAIL" | "SMS" | "MEETING";
export type DealLostReason = "PRICE" | "TIMING" | "COMPETITOR" | "NO_CONTACT" | "PRODUCT_MISMATCH" | "OTHER";
export type DocumentKind =
  | "UPD_STATUS_1"
  | "UPD_STATUS_2"
  | "INVOICE"
  | "WAYBILL"
  | "ACT"
  | "COMMERCIAL_OFFER"
  | "CONTRACT"
  | "RECONCILIATION"
  | "OTHER";
export type DocumentStatus = "DRAFT" | "ISSUED" | "SIGNED" | "VOIDED";
export type SignatureMode = "NONE" | "LIVE_SIGNATURE_SCAN" | "QUALIFIED_ELECTRONIC";

// ─── Pipelines ──────────────────────────────────────────────────────────────

export interface PipelineDto {
  id: string;
  shopId: string;
  name: string;
  kind: PipelineKind;
  description: string | null;
  sortOrder: number;
  isDefault: boolean;
  isActive: boolean;
  stagesCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePipelinePayload {
  name: string;
  kind: PipelineKind;
  description?: string;
  sortOrder?: number;
  isDefault?: boolean;
}

export interface UpdatePipelinePayload {
  name?: string;
  description?: string;
  sortOrder?: number;
  isDefault?: boolean;
  isActive?: boolean;
}

export async function listPipelines(params?: {
  kind?: PipelineKind;
  includeInactive?: boolean;
}): Promise<{ items: PipelineDto[]; total: number }> {
  const search = new URLSearchParams();
  if (params?.kind) search.set("kind", params.kind);
  if (params?.includeInactive) search.set("includeInactive", "true");
  const qs = search.toString();
  return apiFetch(`/admin/crm/pipelines${qs ? `?${qs}` : ""}`);
}

export async function getPipeline(id: string): Promise<PipelineDto> {
  return apiFetch(`/admin/crm/pipelines/${id}`);
}

export async function createPipeline(payload: CreatePipelinePayload): Promise<PipelineDto> {
  return apiFetch(`/admin/crm/pipelines`, { method: "POST", json: payload });
}

export async function updatePipeline(id: string, payload: UpdatePipelinePayload): Promise<PipelineDto> {
  return apiFetch(`/admin/crm/pipelines/${id}`, { method: "PATCH", json: payload });
}

export async function deletePipeline(id: string): Promise<void> {
  await apiFetch(`/admin/crm/pipelines/${id}`, { method: "DELETE" });
}

// ─── Stages (nested) ────────────────────────────────────────────────────────

export interface PipelineStageDto {
  id: string;
  pipelineId: string;
  code: string;
  name: string;
  kind: StageKind;
  color: string | null;
  slaHours: number | null;
  sortOrder: number;
  isActive: boolean;
  dealsCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateStagePayload {
  code: string;
  name: string;
  kind: StageKind;
  color?: string;
  slaHours?: number;
  sortOrder?: number;
}

export interface UpdateStagePayload {
  code?: string;
  name?: string;
  kind?: StageKind;
  color?: string;
  slaHours?: number;
  sortOrder?: number;
  isActive?: boolean;
}

export async function listStages(pipelineId: string): Promise<PipelineStageDto[]> {
  return apiFetch(`/admin/crm/pipelines/${pipelineId}/stages`);
}

export async function createStage(pipelineId: string, payload: CreateStagePayload): Promise<PipelineStageDto> {
  return apiFetch(`/admin/crm/pipelines/${pipelineId}/stages`, { method: "POST", json: payload });
}

export async function updateStage(
  pipelineId: string,
  stageId: string,
  payload: UpdateStagePayload,
): Promise<PipelineStageDto> {
  return apiFetch(`/admin/crm/pipelines/${pipelineId}/stages/${stageId}`, { method: "PATCH", json: payload });
}

export async function deleteStage(pipelineId: string, stageId: string): Promise<void> {
  await apiFetch(`/admin/crm/pipelines/${pipelineId}/stages/${stageId}`, { method: "DELETE" });
}

export async function reorderStages(
  pipelineId: string,
  items: { id: string; sortOrder: number }[],
): Promise<PipelineStageDto[]> {
  return apiFetch(`/admin/crm/pipelines/${pipelineId}/stages/reorder`, {
    method: "POST",
    json: { items },
  });
}

// ─── Deals ──────────────────────────────────────────────────────────────────

export interface DealDto {
  id: string;
  shopId: string;
  pipelineId: string;
  pipelineName: string;
  currentStageId: string;
  currentStageName: string;
  currentStageKind: StageKind;
  currentStageColor: string | null;
  customerId: string;
  customerDisplayName: string | null;
  legalEntityId: string | null;
  ownerUserId: string;
  ownerDisplayName: string | null;
  displayName: string;
  expectedAmountGross: string | null;
  currency: string;
  source: string | null;
  slaDeadlineAt: string | null;
  resultingOrderId: string | null;
  lostReason: DealLostReason | null;
  lostReasonNote: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDealPayload {
  pipelineId: string;
  customerId: string;
  legalEntityId?: string;
  displayName: string;
  expectedAmountGross?: string;
  currency?: string;
  source?: string;
  initialStageId?: string;
  ownerUserId?: string;
}

export interface UpdateDealPayload {
  customerId?: string;
  legalEntityId?: string | null;
  displayName?: string;
  expectedAmountGross?: string | null;
  currency?: string;
  source?: string | null;
  ownerUserId?: string;
}

export interface MoveToStagePayload {
  toStageId: string;
  note?: string;
  lostReason?: DealLostReason;
  lostReasonNote?: string;
}

export async function listDeals(params?: {
  search?: string;
  pipelineId?: string;
  stageId?: string;
  customerId?: string;
  ownerUserId?: string;
  openOnly?: boolean;
  limit?: number;
  offset?: number;
}): Promise<{ items: DealDto[]; total: number }> {
  const search = new URLSearchParams();
  if (params?.search) search.set("search", params.search);
  if (params?.pipelineId) search.set("pipelineId", params.pipelineId);
  if (params?.stageId) search.set("stageId", params.stageId);
  if (params?.customerId) search.set("customerId", params.customerId);
  if (params?.ownerUserId) search.set("ownerUserId", params.ownerUserId);
  if (params?.openOnly) search.set("openOnly", "true");
  if (params?.limit) search.set("limit", String(params.limit));
  if (params?.offset) search.set("offset", String(params.offset));
  const qs = search.toString();
  return apiFetch(`/admin/crm/deals${qs ? `?${qs}` : ""}`);
}

export async function getDeal(id: string): Promise<DealDto> {
  return apiFetch(`/admin/crm/deals/${id}`);
}

export async function createDeal(payload: CreateDealPayload): Promise<DealDto> {
  return apiFetch(`/admin/crm/deals`, { method: "POST", json: payload });
}

export async function updateDeal(id: string, payload: UpdateDealPayload): Promise<DealDto> {
  return apiFetch(`/admin/crm/deals/${id}`, { method: "PATCH", json: payload });
}

export async function moveDealToStage(id: string, payload: MoveToStagePayload): Promise<DealDto> {
  return apiFetch(`/admin/crm/deals/${id}/move-to-stage`, { method: "POST", json: payload });
}

// ─── Activities ─────────────────────────────────────────────────────────────

export interface ActivityDto {
  id: string;
  shopId: string;
  customerId: string;
  dealId: string | null;
  legalEntityId: string | null;
  orderId: string | null;
  kind: ActivityKind;
  title: string;
  content: string | null;
  dueAt: string | null;
  completedAt: string | null;
  completedById: string | null;
  createdById: string;
  createdByName: string | null;
  /** phase19.31 (SC60) — имя клиента, название сделки, номер заказа для строки списка. */
  customerName?: string | null;
  dealTitle?: string | null;
  orderNumber?: string | null;
  /** phase19.31 (crm.md T1–T7) — исполнитель дела; у заметок, писем, SMS — null. */
  assigneeUserId?: string | null;
  assigneeName?: string | null;
  /** Открыта и срок прошёл — считает сервер. */
  overdue?: boolean;
  /** История назначений и выполнения (старые сверху); то же, что `payload.history`. */
  history?: TaskHistoryEntry[];
  payload: unknown;
  createdAt: string;
  updatedAt: string;
}

/** Сотрудник в истории задачи: id и имя на момент события. */
export interface TaskHistoryPerson {
  id: string;
  name: string;
}

/** Запись истории задачи (crm.md T3–T5); `at` — ISO. */
export type TaskHistoryEntry =
  | { type: "ASSIGNED"; at: string; by: TaskHistoryPerson | null; from: TaskHistoryPerson | null; to: TaskHistoryPerson | null }
  | { type: "ASSIGNEE_RELEASED"; at: string; from: TaskHistoryPerson; reason: "staff_released" }
  | { type: "COMPLETED"; at: string; by: TaskHistoryPerson }
  /** Возвращена в работу (контракт §13, с 2026-10-03). */
  | { type: "REOPENED"; at: string; by: TaskHistoryPerson };

/** Числа вкладок страницы «Задачи» — по открытым делам (T7). */
export interface TaskCountsDto {
  all: number;
  mine: number;
  overdue: number;
  unassigned: number;
}

/** Кого можно назначить исполнителем (T2); текущий сотрудник — первым. */
export interface TaskAssigneeOptionDto {
  id: string;
  name: string;
  roleName: string;
  isMe: boolean;
}

export interface CreateActivityPayload {
  /** Клиент; можно не передавать, если есть `orderId` — возьмётся клиент заказа. */
  customerId?: string;
  dealId?: string;
  legalEntityId?: string;
  orderId?: string;
  kind: ActivityKind;
  title: string;
  content?: string;
  dueAt?: string;
  /** Исполнитель задачи, звонка, встречи; не передан — автор (T1). */
  assigneeUserId?: string;
  payload?: Record<string, unknown>;
}

export interface UpdateActivityPayload {
  kind?: ActivityKind;
  title?: string;
  content?: string;
  dueAt?: string | null;
  /** Сменить исполнителя (T3); null — «без исполнителя». */
  assigneeUserId?: string | null;
  payload?: Record<string, unknown>;
}

export async function listActivities(params?: {
  customerId?: string;
  dealId?: string;
  kind?: ActivityKind;
  /** phase19.31 (SC60) — несколько видов сразу: `TASK,CALL,MEETING`. */
  kinds?: ActivityKind[];
  /** phase19.31 (SC60) — `open` невыполненные, `done` выполненные, `all` все. */
  status?: "open" | "done" | "all";
  openTasksOnly?: boolean;
  ownerUserId?: string;
  /** phase19.31 (T7) — «Мои»: назначенные мне. */
  mine?: boolean;
  /** Открытые с прошедшим сроком. */
  overdue?: boolean;
  /** Открытые дела без исполнителя. */
  unassigned?: boolean;
  /** Вернуть `counts` — числа вкладок «Задач». */
  counts?: boolean;
  limit?: number;
  offset?: number;
}): Promise<{ items: ActivityDto[]; total: number; counts?: TaskCountsDto }> {
  const search = new URLSearchParams();
  if (params?.customerId) search.set("customerId", params.customerId);
  if (params?.dealId) search.set("dealId", params.dealId);
  if (params?.kind) search.set("kind", params.kind);
  if (params?.kinds?.length) search.set("kinds", params.kinds.join(","));
  if (params?.status) search.set("status", params.status);
  if (params?.openTasksOnly) search.set("openTasksOnly", "true");
  if (params?.ownerUserId) search.set("ownerUserId", params.ownerUserId);
  if (params?.mine) search.set("mine", "true");
  if (params?.overdue) search.set("overdue", "true");
  if (params?.unassigned) search.set("unassigned", "true");
  if (params?.counts) search.set("counts", "true");
  if (params?.limit) search.set("limit", String(params.limit));
  if (params?.offset) search.set("offset", String(params.offset));
  const qs = search.toString();
  return apiFetch(`/admin/crm/activities${qs ? `?${qs}` : ""}`);
}

/** Одна запись с именами связей, исполнителем и историей. */
export async function getActivity(id: string): Promise<ActivityDto> {
  return apiFetch(`/admin/crm/activities/${id}`);
}

/** Кого можно назначить исполнителем задачи (T2). */
export async function listTaskAssignees(): Promise<TaskAssigneeOptionDto[]> {
  return apiFetch(`/admin/crm/task-assignees`);
}

export async function createActivity(payload: CreateActivityPayload): Promise<ActivityDto> {
  return apiFetch(`/admin/crm/activities`, { method: "POST", json: payload });
}

export async function updateActivity(id: string, payload: UpdateActivityPayload): Promise<ActivityDto> {
  return apiFetch(`/admin/crm/activities/${id}`, { method: "PATCH", json: payload });
}

export async function completeActivity(id: string, resolutionNote?: string): Promise<ActivityDto> {
  return apiFetch(`/admin/crm/activities/${id}/complete`, {
    method: "POST",
    json: { resolutionNote },
  });
}

/**
 * «Вернуть в работу» (контракт §13): снимает отметку о выполнении, срок и
 * исполнитель не меняются, в истории — `REOPENED`. Могут те же, кто может
 * отметить выполненной, иначе 403 `activity_reopen_forbidden`.
 */
export async function reopenActivity(id: string): Promise<ActivityDto> {
  return apiFetch(`/admin/crm/activities/${id}/reopen`, { method: "POST" });
}

export async function deleteActivity(id: string): Promise<void> {
  await apiFetch(`/admin/crm/activities/${id}`, { method: "DELETE" });
}

export async function customerActivityFeed(
  customerId: string,
  params?: { limit?: number; offset?: number },
): Promise<{ items: ActivityDto[]; total: number }> {
  const search = new URLSearchParams();
  if (params?.limit) search.set("limit", String(params.limit));
  if (params?.offset) search.set("offset", String(params.offset));
  const qs = search.toString();
  return apiFetch(`/admin/crm/customers/${customerId}/activity-feed${qs ? `?${qs}` : ""}`);
}

// ─── LegalProfiles ──────────────────────────────────────────────────────────

export interface LegalProfileDto {
  id: string;
  shopId: string;
  displayName: string;
  legalName: string;
  inn: string;
  kpp: string | null;
  ogrn: string | null;
  legalAddress: string;
  /** phase19 — контактный телефон (fallback для самовывоза checkout). */
  phone: string | null;
  bankDetails: unknown;
  vatPayer: boolean;
  defaultVatRate: number | null;
  defaultSignerName: string;
  defaultSignerPosition: string;
  defaultSignerBasis: string;
  signatureScanKey: string | null;
  stampScanKey: string | null;
  documentPrefix: unknown;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLegalProfilePayload {
  displayName: string;
  legalName: string;
  inn: string;
  kpp?: string;
  ogrn?: string;
  legalAddress: string;
  /** phase19 — контактный телефон (fallback для самовывоза checkout). */
  phone?: string;
  bankDetails?: Record<string, unknown>;
  vatPayer?: boolean;
  defaultVatRate?: number;
  defaultSignerName: string;
  defaultSignerPosition: string;
  defaultSignerBasis: string;
  signatureScanKey?: string;
  stampScanKey?: string;
  documentPrefix?: Record<string, string>;
  isDefault?: boolean;
}

export type UpdateLegalProfilePayload = Partial<CreateLegalProfilePayload> & {
  isActive?: boolean;
};

export async function listLegalProfiles(params?: {
  includeInactive?: boolean;
}): Promise<{ items: LegalProfileDto[]; total: number }> {
  const search = new URLSearchParams();
  if (params?.includeInactive) search.set("includeInactive", "true");
  const qs = search.toString();
  return apiFetch(`/admin/shop/legal-profiles${qs ? `?${qs}` : ""}`);
}

export async function getLegalProfile(id: string): Promise<LegalProfileDto> {
  return apiFetch(`/admin/shop/legal-profiles/${id}`);
}

export async function createLegalProfile(payload: CreateLegalProfilePayload): Promise<LegalProfileDto> {
  const created = await apiFetch<LegalProfileDto>(`/admin/shop/legal-profiles`, { method: "POST", json: payload });
  // Реквизиты продавца — проверка сайта SB125: плашка «Сайт работает без…» перечитается.
  invalidateLiveSiteIssues();
  return created;
}

export async function updateLegalProfile(
  id: string,
  payload: UpdateLegalProfilePayload,
): Promise<LegalProfileDto> {
  const updated = await apiFetch<LegalProfileDto>(`/admin/shop/legal-profiles/${id}`, { method: "PATCH", json: payload });
  invalidateLiveSiteIssues();
  return updated;
}

export async function deleteLegalProfile(id: string): Promise<void> {
  await apiFetch(`/admin/shop/legal-profiles/${id}`, { method: "DELETE" });
  invalidateLiveSiteIssues();
}

// ─── DocumentTemplates ──────────────────────────────────────────────────────

export interface DocumentTemplateDto {
  id: string;
  shopId: string;
  kind: DocumentKind;
  name: string;
  htmlSource: string;
  variables: unknown;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDocumentTemplatePayload {
  kind: DocumentKind;
  name: string;
  htmlSource: string;
  variables?: Record<string, unknown>;
  isDefault?: boolean;
}

export type UpdateDocumentTemplatePayload = Partial<Omit<CreateDocumentTemplatePayload, "kind">> & {
  isActive?: boolean;
};

export async function listDocumentTemplates(params?: {
  kind?: DocumentKind;
  includeInactive?: boolean;
}): Promise<{ items: DocumentTemplateDto[]; total: number }> {
  const search = new URLSearchParams();
  if (params?.kind) search.set("kind", params.kind);
  if (params?.includeInactive) search.set("includeInactive", "true");
  const qs = search.toString();
  return apiFetch(`/admin/crm/document-templates${qs ? `?${qs}` : ""}`);
}

export async function getDocumentTemplate(id: string): Promise<DocumentTemplateDto> {
  return apiFetch(`/admin/crm/document-templates/${id}`);
}

export async function createDocumentTemplate(payload: CreateDocumentTemplatePayload): Promise<DocumentTemplateDto> {
  return apiFetch(`/admin/crm/document-templates`, { method: "POST", json: payload });
}

export async function updateDocumentTemplate(
  id: string,
  payload: UpdateDocumentTemplatePayload,
): Promise<DocumentTemplateDto> {
  return apiFetch(`/admin/crm/document-templates/${id}`, { method: "PATCH", json: payload });
}

export async function deleteDocumentTemplate(id: string): Promise<void> {
  await apiFetch(`/admin/crm/document-templates/${id}`, { method: "DELETE" });
}

// ─── Documents ──────────────────────────────────────────────────────────────

export interface DocumentDto {
  id: string;
  shopId: string;
  kind: DocumentKind;
  templateId: string | null;
  templateName: string | null;
  legalProfileId: string;
  legalProfileName: string;
  dealId: string | null;
  orderId: string | null;
  /**
   * Номер связанного заказа для обратной плитки «Заказ» в шапке документа
   * (phase19.24 §4). `undefined` — сервер поле ещё не отдаёт, плитка
   * показывает «Открыть заказ».
   */
  orderNumber?: string | null;
  customerId: string | null;
  legalEntityId: string | null;
  number: string;
  variablesSnapshot: unknown;
  status: DocumentStatus;
  signatureMode: SignatureMode;
  pdfStorageKey: string | null;
  pdfSizeBytes: number | null;
  totalGross: string | null;
  totalVat: string | null;
  currency: string;
  issuedAt: string | null;
  issuedById: string | null;
  signedAt: string | null;
  signedById: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Покупатель у любого статуса (контракт §19): у выпущенного — из снимка
   * печати, у черновика — клиент/юрлицо документа, сделки или заказа; null —
   * не определён. ИНН у физлица — null.
   */
  counterpartyName: string | null;
  counterpartyInn: string | null;
  /** Название сделки; null — без сделки. */
  dealName: string | null;
  /** Кто выпустил / подписал (имя, иначе email); null — ещё нет или сотрудник удалён. */
  issuedByName: string | null;
  signedByName: string | null;
  /** Реквизиты продавца (профиль документа) не заполнены — по самим полям. */
  legalProfileIncomplete: boolean;
  /** Чего не хватает в реквизитах продавца. */
  legalProfileGaps: SellerProfileGap[];
  /**
   * Позиции — только в карточке (`GET :id`): выпущенный — из снимка печати,
   * черновик — из заказа документа (как их напечатает выпуск); null — без
   * заказа позиций нет.
   */
  items?: DocumentItemDto[] | null;
}

/** Чего не хватает в реквизитах продавца (контракт §19). */
export type SellerProfileGap = "legalName" | "inn" | "kpp" | "ogrn" | "legalAddress" | "bankAccount" | "bik" | "signer";

/** Позиция документа, как её напечатает (или напечатал) выпуск. */
export interface DocumentItemDto {
  index: number;
  name: string;
  /** Числа строкой, как в печатной форме. */
  quantity: string;
  price: string;
  total: string;
  vatRate: number | null;
}

export interface CreateDocumentPayload {
  kind: DocumentKind;
  templateId?: string;
  legalProfileId?: string;
  dealId?: string;
  /**
   * Заказ-основание: счёт и УПД, выставленные из карточки заказа, обязаны
   * попадать в её вкладку «Оплаты и документы» и в узлы «Пути заказа» —
   * список едет серверным фильтром `orderId` (phase19.24 §9).
   */
  orderId?: string;
  customerId?: string;
  legalEntityId?: string;
  variablesSnapshot?: Record<string, unknown>;
  totalGross?: string;
  totalVat?: string;
  currency?: string;
  notes?: string;
}

export async function listDocuments(params?: {
  kind?: DocumentKind;
  status?: DocumentStatus;
  dealId?: string;
  /** Документы конкретного заказа — вкладка «Оплаты и документы» (phase19.24 §9). */
  orderId?: string;
  customerId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: DocumentDto[]; total: number }> {
  const search = new URLSearchParams();
  if (params?.kind) search.set("kind", params.kind);
  if (params?.status) search.set("status", params.status);
  if (params?.dealId) search.set("dealId", params.dealId);
  if (params?.orderId) search.set("orderId", params.orderId);
  if (params?.customerId) search.set("customerId", params.customerId);
  if (params?.search) search.set("search", params.search);
  if (params?.limit) search.set("limit", String(params.limit));
  if (params?.offset) search.set("offset", String(params.offset));
  const qs = search.toString();
  return apiFetch(`/admin/crm/documents${qs ? `?${qs}` : ""}`);
}

export async function getDocument(id: string): Promise<DocumentDto> {
  return apiFetch(`/admin/crm/documents/${id}`);
}

export async function createDocument(payload: CreateDocumentPayload): Promise<DocumentDto> {
  return apiFetch(`/admin/crm/documents`, { method: "POST", json: payload });
}

export async function issueDocument(
  id: string,
  payload?: { templateId?: string; legalProfileId?: string },
): Promise<DocumentDto> {
  return apiFetch(`/admin/crm/documents/${id}/issue`, {
    method: "POST",
    json: payload ?? {},
  });
}

export async function signDocument(id: string, signatureMode: SignatureMode): Promise<DocumentDto> {
  return apiFetch(`/admin/crm/documents/${id}/sign`, {
    method: "POST",
    json: { signatureMode },
  });
}

export async function voidDocument(id: string, voidReason: string): Promise<DocumentDto> {
  return apiFetch(`/admin/crm/documents/${id}/void`, {
    method: "POST",
    json: { voidReason },
  });
}

export async function deleteDocument(id: string): Promise<void> {
  await apiFetch(`/admin/crm/documents/${id}`, { method: "DELETE" });
}

export interface DocumentPdfInfo {
  url: string;
  sizeBytes: number | null;
  number: string;
}

export async function getDocumentPdfUrl(id: string): Promise<DocumentPdfInfo> {
  return apiFetch(`/admin/crm/documents/${id}/pdf`);
}

export async function regenerateDocumentPdf(id: string): Promise<DocumentDto> {
  return apiFetch(`/admin/crm/documents/${id}/regenerate-pdf`, {
    method: "POST",
  });
}

/**
 * PDF документа файлом (phase19.31): «Скачать PDF» и «Печать» в карточке
 * документа. Файл отдаёт сам API под cookie, поэтому простой ссылкой его не
 * открыть — забираем blob (`apiDownload`), ошибки приходят `ApiError`.
 */
export async function downloadDocumentPdf(id: string, number: string): Promise<ApiFileResponse> {
  return apiDownload(
    `/admin/crm/documents/${id}/pdf/file`,
    { headers: { Accept: "application/pdf" } },
    `${number || "document"}.pdf`,
  );
}

// ─── Display labels ─────────────────────────────────────────────────────────

export const PIPELINE_KIND_LABEL: Record<PipelineKind, string> = {
  SALES_B2C: "B2C — Розница",
  SALES_B2B: "B2B — Корпоративные",
  RETURNS: "Возвраты",
  RECLAMATION: "Рекламации",
  CUSTOM: "Свой",
};

/**
 * Короткие подписи вида воронки — для пилюль-фильтров и второй линии строки
 * (phase19.31): те же слова, что в полных подписях выше, без «B2C — ».
 */
export const PIPELINE_KIND_SHORT: Record<PipelineKind, string> = {
  SALES_B2C: "Розница",
  SALES_B2B: "Корпоративные",
  RETURNS: "Возвраты",
  RECLAMATION: "Рекламации",
  CUSTOM: "Свои",
};

/**
 * Вид стадии словами (phase19.31: «Победа» / «Проигрыш» заменены на
 * человеческие — так вид стадии читается и в настройках, и в карточке сделки).
 */
export const STAGE_KIND_LABEL: Record<StageKind, string> = {
  INITIAL: "Начальная",
  IN_PROGRESS: "В работе",
  WON: "Успешная",
  LOST: "Не состоялась",
  CANCELLED: "Отменена",
  ON_HOLD: "На паузе",
};

export const ACTIVITY_KIND_LABEL: Record<ActivityKind, string> = {
  NOTE: "Заметка",
  TASK: "Задача",
  CALL: "Звонок",
  EMAIL: "Письмо",
  SMS: "SMS",
  MEETING: "Встреча",
};

export const DEAL_LOST_REASON_LABEL: Record<DealLostReason, string> = {
  PRICE: "Цена",
  TIMING: "Сроки",
  COMPETITOR: "Конкурент",
  NO_CONTACT: "Нет связи",
  PRODUCT_MISMATCH: "Не подходит товар",
  OTHER: "Другое",
};

export const DOCUMENT_KIND_LABEL: Record<DocumentKind, string> = {
  UPD_STATUS_1: "УПД (статус 1, с НДС)",
  UPD_STATUS_2: "УПД (статус 2, без НДС)",
  INVOICE: "Счёт на оплату",
  WAYBILL: "Накладная (Торг-12)",
  ACT: "Акт",
  COMMERCIAL_OFFER: "Коммерческое предложение",
  CONTRACT: "Договор",
  RECONCILIATION: "Акт сверки",
  OTHER: "Иное",
};

/**
 * Короткие подписи вида документа (phase19.31) — для строки реестра, где
 * рядом стоит номер: «Счёт СЧТ-2026-00004», «УПД УПД-2026-012136».
 */
export const DOCUMENT_KIND_SHORT: Record<DocumentKind, string> = {
  UPD_STATUS_1: "УПД",
  UPD_STATUS_2: "УПД",
  INVOICE: "Счёт",
  WAYBILL: "Накладная",
  ACT: "Акт",
  COMMERCIAL_OFFER: "КП",
  CONTRACT: "Договор",
  RECONCILIATION: "Акт сверки",
  OTHER: "Документ",
};

export const DOCUMENT_STATUS_LABEL: Record<DocumentStatus, string> = {
  DRAFT: "Черновик",
  ISSUED: "Выпущен",
  SIGNED: "Подписан",
  VOIDED: "Аннулирован",
};

/** Способ подписи словами — вместо кодов `LIVE_SIGNATURE_SCAN` (phase19.31). */
export const SIGNATURE_MODE_LABEL: Record<SignatureMode, string> = {
  NONE: "не подписан",
  LIVE_SIGNATURE_SCAN: "скан живой подписи",
  QUALIFIED_ELECTRONIC: "электронная подпись (КЭП)",
};

// ─── DealItems (phase10.5 шаг 2.6) ──────────────────────────────────────────

export interface DealItemDto {
  id: string;
  dealId: string;
  skuId: string | null;
  productId: string | null;
  productName: string;
  skuCode: string | null;
  gtin: string | null;
  saleUnit: string | null;
  saleQuantity: string | null;
  vatRate: number;
  quantity: string;
  priceGross: string;
  discountAmount: string;
  totalGross: string;
  note: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export async function listDealItems(dealId: string): Promise<DealItemDto[]> {
  return apiFetch<DealItemDto[]>(`/admin/crm/deals/${dealId}/items`);
}

export async function addDealItem(
  dealId: string,
  payload: {
    skuId: string;
    quantity?: string;
    priceGross?: string;
    discountAmount?: string;
    note?: string;
  },
): Promise<DealItemDto> {
  return apiFetch<DealItemDto>(`/admin/crm/deals/${dealId}/items`, {
    method: "POST",
    json: payload,
  });
}

export async function addDealItemsBulk(
  dealId: string,
  skuIds: string[],
): Promise<DealItemDto[]> {
  return apiFetch<DealItemDto[]>(`/admin/crm/deals/${dealId}/items/bulk`, {
    method: "POST",
    json: { skuIds },
  });
}

export async function updateDealItem(
  dealId: string,
  itemId: string,
  payload: {
    quantity?: string;
    priceGross?: string;
    discountAmount?: string;
    note?: string;
    sortOrder?: number;
  },
): Promise<DealItemDto> {
  return apiFetch<DealItemDto>(
    `/admin/crm/deals/${dealId}/items/${itemId}`,
    { method: "PATCH", json: payload },
  );
}

export async function removeDealItem(
  dealId: string,
  itemId: string,
): Promise<void> {
  await apiFetch(`/admin/crm/deals/${dealId}/items/${itemId}`, {
    method: "DELETE",
  });
}
